import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import Card from "../../../UI/Card/Card";
import Button from "../../../UI/Button/Button"
import ErrorPanel, { ErrorSize } from "../../../UI/ErrorPanel/ErrorPanel";

import { faRocket, faPlayCircle, faStopCircle } from "@fortawesome/free-solid-svg-icons";

import * as ApplicationConstant from "../../../Constants/ApplicationConstant";
import * as ApplicationsUtils from "../../../Utils/ApplicationsUtils";
import * as ErrorUtils from "../../../Utils/ErrorUtils";
import * as ApplicationsActions from "../../../../middleware/Store/Actions/ApplicationsActions";
import * as SandBoxApplicationsActions from "../../../../middleware/Store/Actions/SandBoxApplicationsActions";

import LogService from "../../../../middleware/CommonSdk/LogService";
const LOG_TAG = "APP-EDITION-DEPLOYMENT";

const Deployment = (props) => {

    const intl = useIntl();
    const [application, setApplication] = useState(null);
    const [progress, setProgress] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        LogService.trace(LOG_TAG, "useEffect application");
        if (props.applicationId) {
            setApplication(ApplicationsUtils.getApplicationById(props.applicationId, props.applications));

            if (props.applicationId !== application?.id) {
                setProgress(false);
                setErrorMessage("");
            }
        }
    }, [props.applications, props.applicationId]);

    const handleApplicationDeployRequest = (event) => {
        LogService.trace(LOG_TAG, "handleApplicationDeployRequest", event.target.value);

        setProgress(true);
        props.deployRequest(application)
            .then(() => {
                LogService.debug(LOG_TAG, "handleApplicationDeployRequest success");
                setProgress(false);
                if (props.onActionSuccess) {
                    props.onActionSuccess();
                }
            })
            .catch((errorPayload) => {
                LogService.debug(LOG_TAG, "handleApplicationDeployRequest error");
                setProgress(false);
                setErrorMessage(ErrorUtils.getPaymentPrettyErrorMessage(errorPayload, intl));
                if (props.onActionFailed) {
                    props.onActionFailed();
                }
            });
    };

    const handleApplicationStop = (event) => {
        LogService.trace(LOG_TAG, "handleApplicationStop", event.target.value);

        setProgress(true);
        props.stop(application.id)
            .then(() => {
                LogService.debug(LOG_TAG, "handleApplicationStop success");
                setProgress(false);
                if (props.onActionSuccess) {
                    props.onActionSuccess();
                }
            })
            .catch((errorPayload) => {
                LogService.debug(LOG_TAG, "handleApplicationStop error");
                setProgress(false);
                setErrorMessage(ErrorUtils.getPrettyErrorMessage(errorPayload, intl));
                if (props.onActionFailed) {
                    props.onActionFailed();
                }
            });
    };

    const handleApplicationRestart = (event) => {
        LogService.trace(LOG_TAG, "handleApplicationRestart", event.target.value);

        setProgress(true);
        props.restart(application.id)
            .then(() => {
                LogService.debug(LOG_TAG, "handleApplicationRestart success");
                setProgress(false);
                if (props.onActionSuccess) {
                    props.onActionSuccess();
                }
            })
            .catch((errorPayload) => {
                LogService.debug(LOG_TAG, "handleApplicationRestart error");
                setProgress(false);
                setErrorMessage(ErrorUtils.getPrettyErrorMessage(errorPayload, intl));
                if (props.onActionFailed) {
                    props.onActionFailed();
                }
            });
    };

    LogService.trace(LOG_TAG, "render");
    const { label, className } = ApplicationsUtils.getApplicationStateAndStyle(application);

    let errorMessageDisplay = null;
    if (errorMessage) {
        errorMessageDisplay =
            <div className="mt-2">
                <ErrorPanel size={ErrorSize.Small} >{errorMessage}</ErrorPanel>
            </div>
    }

    let actionsDisplay = null;
    if (application && props.isUpdateEnabled) {
        if (application.env === ApplicationConstant.Env.Deployed && (application.state === ApplicationConstant.State.Active || application.state === ApplicationConstant.State.New)) {
            actionsDisplay =
                <div>
                    < div className="panel bg-fog mb-3" > {intl.messages.applicationEditionDeploymentStopRestartMessage}</div >
                    <div>
                        <Button
                            label={intl.messages.applicationEditionDeploymentStopButton}
                            icon={faStopCircle}
                            onClick={handleApplicationStop}
                            spinner={progress}
                            disabled={progress}
                            className="btn-submit"
                        />
                        {errorMessageDisplay}
                    </div>
                </div>
        } else if (application.env === ApplicationConstant.Env.Deployed && application.state === ApplicationConstant.State.Stopped) {
            actionsDisplay =
                <div>
                    <div className="panel bg-fog mb-3">{intl.messages.applicationEditionDeploymentStopRestartMessage}</div>
                    <div>
                        <Button
                            label={intl.messages.applicationEditionDeploymentRestartButton}
                            icon={faPlayCircle}
                            onClick={handleApplicationRestart}
                            spinner={progress}
                            disabled={progress}
                            className="btn-submit"
                        />
                        {errorMessageDisplay}
                    </div>
                </div>
        } else if (application.env === ApplicationConstant.Env.NotDeployed) {
            let isInvoicesFullyFilled = application.kpi !== ApplicationConstant.ModelType.PayAsYouGo || ApplicationsUtils.isInvoiceAccountFullyFilled(props.invoicesAccount);
            actionsDisplay =
                <div>
                    <div className="panel bg-fog mb-3">{intl.messages.applicationEditionDeploymentRequestMessage}</div>
                    <div>
                        <Button
                            label={intl.messages.applicationEditionDeploymentButton}
                            icon={faRocket}
                            onClick={handleApplicationDeployRequest}
                            spinner={progress}
                            disabled={progress || !isInvoicesFullyFilled}
                            className="btn-submit"
                            tooltipTitle={isInvoicesFullyFilled ? "" : intl.messages.applicationEditionDeploymentInvoicesAddressErrorMessage}
                        />
                        {errorMessageDisplay}
                    </div>
                </div>
        }
    }

    return (
        <Card title={intl.messages.applicationEditionDeploymentTitle} header={props.showHeader}>
            <table className="table">
                <tbody>
                    <tr>
                        <td className="table-label">{intl.messages.applicationEditionDeploymentStateTitle}</td>
                        <td className="text-right"><span className={"badge-dashed " + className}>{label}</span></td>
                    </tr>
                </tbody>
            </table>
            {actionsDisplay}
        </Card >
    );
}

Deployment.propTypes = {
    applicationId: PropTypes.string,
    isUpdateEnabled: PropTypes.bool,
    onActionSuccess: PropTypes.func,
    onActionFailed: PropTypes.func,
    showHeader: PropTypes.bool,
    isSandBoxApplication: PropTypes.bool
};

Deployment.defaultProps = {
    applicationId: "",
    isUpdateEnabled: false,
    onActionSuccess: null,
    onActionFailed: null,
    showHeader: true,
    isSandBoxApplication: false
}

const mapStateToProps = (state, props) => {
    return {
        applications: props.isSandBoxApplication ? state.sandBoxApplications.applications : state.applications.applications,
        invoicesAccount: state.invoices.account
    };
};

const mapDispatchToProps = (dispatch, props) => {
    return {
        deployRequest: (app) => dispatch(props.isSandBoxApplication ? SandBoxApplicationsActions.deployApplicationRequest(app) : ApplicationsActions.deployApplicationRequest(app)), //should not happen on sandbox tbc for superadmin role
        stop: (appId) => dispatch(props.isSandBoxApplication ? SandBoxApplicationsActions.stopApplication(appId) : ApplicationsActions.stopApplication(appId)),
        restart: (appId) => dispatch(props.isSandBoxApplication ? SandBoxApplicationsActions.restartApplication(appId) : ApplicationsActions.restartApplication(appId))
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(Deployment);